import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  ButtonBase,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import moment from "moment-timezone";

import ArrowBackIcon from "../../../assets/icons/back.svg";
import PaymentIcon from "../../../assets/icons/payment.svg";
import "./book-package.scss";
import { getBookingAppointmentLeadById } from "../../../api/provider-section/provider-service-management";
import {
  getPreneedPackages,
  bookPackageForLead,
} from "../../../api/preneed-section/preneed-booking-appointment.api";

//  MUI Icons (for Lead info + fields)
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

type PackageCategory = {
  id: number;
  name: string;
};

type PackageItem = {
  id: number;
  packageName: string;
  price: number;
  description?: string;
  categories: PackageCategory[];
};

function isPackageActive(item: any): boolean {
  const status = String(item?.status ?? "")
    .trim()
    .toLowerCase();

  if (typeof item?.isEnabled === "boolean") return item.isEnabled;
  if (typeof item?.isActive === "boolean") return item.isActive;
  if (status) return status === "active";

  return true;
}

function mapPackageFromApi(item: any): PackageItem {
  return {
    id: Number(item.id ?? item.packageId),
    packageName: item.packageName ?? item.name ?? "",
    price: Number(item.price ?? 0),
    description: item.description ?? "",
    categories: Array.isArray(item.categories)
      ? item.categories.map((cat: any) => ({
          id: Number(cat.id),
          name: cat.name ?? "",
        }))
      : [],
  };
}

/** ---------------- Types ---------------- */
type LeadAddress = {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
};

export type LeadData = {
  id: number;
  leadName: string;
  email: string;
  phoneNumber: string;
  address?: LeadAddress;
};

interface BookingPackageProps {
  leadId: number;
  onBack: () => void;
  initialLead?: LeadData | null;
}

const REQUIRE_DATE = false;
const REQUIRE_TIME = false;

const bookingPackageSchema = Yup.object({
  package: Yup.object()
    .shape({
      id: Yup.number().required(),
      packageName: Yup.string().required(),
      price: Yup.number().required(),
    })
    .nullable()
    .required("Please select a package."),

  serviceDate: REQUIRE_DATE
    ? Yup.mixed()
        .nullable()
        .required("Service date is required.")
    : Yup.mixed().nullable(),

 serviceTime: REQUIRE_TIME
  ? Yup.mixed()
      .nullable()
      .required("Service time is required.")
      .test(
        "not-in-past",
        "Past time is not allowed.",
        function (value) {
          if (!value) return false;

          const serviceDate = this.parent.serviceDate;
          if (!serviceDate) return true;

          const selectedDate = dayjs(serviceDate);
          const selectedTime = value as Dayjs;
          const now = dayjs();

          if (!selectedDate.isValid() || !selectedTime?.isValid?.()) return false;

          // Only apply past-time restriction when selected date is today
          if (!selectedDate.isSame(now, "day")) return true;

          const combinedDateTime = selectedDate
            .hour(selectedTime.hour())
            .minute(selectedTime.minute())
            .second(0)
            .millisecond(0);

          return combinedDateTime.isAfter(now);
        }
      )
  : Yup.mixed().nullable(),
});

function normalizeLeadFromApi(raw: unknown): LeadData | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === "number" ? o.id : Number(o.id);
  if (Number.isNaN(id)) return null;

  const leadName =
    typeof o.leadName === "string"
      ? o.leadName
      : typeof o.name === "string"
      ? o.name
      : "";

  const email = typeof o.email === "string" ? o.email : "";

  const phoneNumber =
    typeof o.phoneNumber === "string"
      ? o.phoneNumber
      : typeof o.phone === "string"
      ? o.phone
      : "";

  const addr = o.address;
  let address: LeadAddress | undefined;

  if (addr && typeof addr === "object") {
    const a = addr as Record<string, unknown>;
    address = {
      street: (a.street_line_1 || a.street1 || a.street || "") as string,
      street2: (a.street_line_2 || a.street2 || "") as string,
      city: (a.city as string) || "",
      state: (a.state as string) || "",
      zipcode: (a.zipcode as string) || "",
      country: (a.country as string) || "",
    };
  }

  return { id, leadName, email, phoneNumber, address };
}

const BookingPackage: React.FC<BookingPackageProps> = ({
  leadId,
  onBack,
  initialLead,
}) => {
  const [lead, setLead] = useState<LeadData | null>(initialLead ?? null);
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [pageLoading, setPageLoading] = useState(true);

  const [snack, setSnack] = useState<{
    open: boolean;
    type: "success" | "error";
    message: string;
  }>({ open: false, type: "success", message: "" });

  useEffect(() => {
    const load = async () => {
      setPageLoading(true);
      try {
        if (initialLead) {
          setLead(initialLead);
          const pkgRes = await getPreneedPackages();
          const list =
            pkgRes?.data?.data?.data ??
            pkgRes?.data?.data ??
            pkgRes?.data ??
            [];
          setPackages(
            Array.isArray(list)
              ? list.filter(isPackageActive).map(mapPackageFromApi)
              : [],
          );
        } else {
          const [leadRes, pkgRes] = await Promise.all([
            getBookingAppointmentLeadById(leadId),
            getPreneedPackages(),
          ]);

          const raw =
            leadRes?.data?.data?.data ??
            leadRes?.data?.data?.lead ??
            leadRes?.data?.data;

          const leadData = normalizeLeadFromApi(raw);
          setLead(leadData ?? null);

          const list =
            pkgRes?.data?.data?.data ??
            pkgRes?.data?.data ??
            pkgRes?.data ??
            [];
          setPackages(
            Array.isArray(list)
              ? list.filter(isPackageActive).map(mapPackageFromApi)
              : [],
          );
        }
      } catch (e) {
        setLead(initialLead ?? null);
        setPackages([]);
        if (!initialLead) {
          setSnack({
            open: true,
            type: "error",
            message: "Failed to load lead/packages. Please try again.",
          });
        }
      } finally {
        setPageLoading(false);
      }
    };

    load();
  }, [leadId, initialLead]);

  const formik = useFormik({
    initialValues: {
      package: null as PackageItem | null,
      serviceDate: null as Dayjs | null,
      serviceTime: null as Dayjs | null,
    },
    validationSchema: bookingPackageSchema,
    onSubmit: async (values, helpers) => {
      if (!values.package) return;

      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

      const dateStr = values.serviceDate
        ? dayjs(values.serviceDate).format("YYYY-MM-DD")
        : "";
      const timeStr = values.serviceTime
        ? dayjs(values.serviceTime).format("HH:mm")
        : "";

      const serviceDateISO =
        dateStr && timeStr
          ? moment
              .tz(
                `${dateStr}T${timeStr}:00`,
                "YYYY-MM-DDTHH:mm:ss",
                timezone
              )
              .format("YYYY-MM-DDTHH:mm:ss")
          : "";

      try {
        const res = await bookPackageForLead(leadId, {
          packageId: values.package.id,
          serviceDate: serviceDateISO,
        });

        const success = res?.data?.success ?? res?.status === 200;

        if (success) {
          helpers.resetForm();
          setSnack({
            open: true,
            type: "success",
            message:
              res?.data?.message ??
              "Payment link has been successfully sent to the customer.",
          });
        } else {
          setSnack({
            open: true,
            type: "error",
            message:
              res?.data?.message ?? "Failed to send payment link. Please try again.",
          });
        }
      } catch (e: any) {
        setSnack({
          open: true,
          type: "error",
          message:
            e?.response?.data?.message ??
            "Something went wrong while sending the payment link.",
        });
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const addressText = useMemo(() => {
    const a = lead?.address;
    if (!a) return "-";
    const parts = [a.street, a.street2, a.city, a.state, a.zipcode, a.country].filter(Boolean);
    return parts.length ? parts.join(", ") : "-";
  }, [lead]);

  const selectedPackage = formik.values.package;
  const totalAmount = useMemo(
    () => Number(selectedPackage?.price || 0),
    [selectedPackage]
  );
  const isSelectedDateToday = useMemo(() => {
    return formik.values.serviceDate
      ? dayjs(formik.values.serviceDate).isSame(dayjs(), "day")
      : false;
  }, [formik.values.serviceDate]);

const minSelectableTime = useMemo(() => {
  return isSelectedDateToday ? dayjs() : undefined;
}, [isSelectedDateToday]);

const now = dayjs();

const shouldDisableTime = (
  value: any,
  view: "hours" | "minutes" | "seconds"
) => {
  if (!formik.values.serviceDate) return false;

  const now = dayjs();
  const selectedDate = dayjs(formik.values.serviceDate);

  if (!selectedDate.isSame(now, "day")) return false;

  const timeValue = value as Dayjs;

  if (view === "hours") {
    return timeValue.hour() < now.hour();
  }

  if (view === "minutes") {
    const selectedTime = formik.values.serviceTime;
    if (!selectedTime) return false;

    return (
      dayjs(selectedTime).hour() === now.hour() &&
      timeValue.minute() < now.minute()
    );
  }

  return false;
};

  if (pageLoading) {
    return (
      <Box className="booking-package booking-package--loading">
        <CircularProgress size={42} />
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box className="booking-package booking-package--error">
        <Typography className="booking-package__errorText">Lead not found.</Typography>
        <Button variant="outlined" onClick={onBack} sx={{ mt: 2 }}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  return (
    <Box className="booking-package">
      <Box className="booking-package__scroll">
        <Box className="booking-package__container">
          <ButtonBase
            className="booking-package__back"
            onClick={onBack}
            sx={{ alignItems: "center", justifyContent: "flex-start", width: "fit-content" }}
          >
            <img
              src={ArrowBackIcon}
              alt="back"
              className="booking-package__backIcon"
            />
            <Typography className="booking-package__backText">
              Back to Leads Detail
            </Typography>
          </ButtonBase>

          <Grid container spacing={3} className="booking-package__grid" alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Paper elevation={0} className="booking-package__card">
                <Typography className="booking-package__title">
                  Order Package &amp; Send Payment Link
                </Typography>
                <Typography className="booking-package__subtitle">
                  Configure package details and send payment link to the lead
                </Typography>

                <Paper elevation={0} className="booking-package__leadInfo">
                  <Typography className="booking-package__sectionTitle">
                    Lead Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box className="booking-package__infoRow">
                        <PersonOutlineIcon className="booking-package__infoIcon" />
                        <Box className="booking-package__infoText">
                          <Typography className="booking-package__label">Name</Typography>
                          <Typography className="booking-package__value">
                            {lead.leadName || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box className="booking-package__infoRow">
                        <MailOutlineIcon className="booking-package__infoIcon" />
                        <Box className="booking-package__infoText">
                          <Typography className="booking-package__label">Email</Typography>
                          <Typography className="booking-package__value">
                            {lead.email || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box className="booking-package__infoRow">
                        <LocalPhoneOutlinedIcon className="booking-package__infoIcon" />
                        <Box className="booking-package__infoText">
                          <Typography className="booking-package__label">Phone Number</Typography>
                          <Typography className="booking-package__value">
                            {lead.phoneNumber || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box className="booking-package__infoRow">
                        <LocationOnOutlinedIcon className="booking-package__infoIcon" />
                        <Box className="booking-package__infoText">
                          <Typography className="booking-package__label">Address</Typography>
                          <Typography className="booking-package__value">
                            {addressText}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Divider className="booking-package__divider" />

                <form onSubmit={formik.handleSubmit}>
                  <Box className="booking-package__fieldBlock">
                    <Typography className="booking-package__fieldLabel" sx={{mb: '8px !important'}}>
                      Select Package <span className="booking-package__required">*</span>
                    </Typography>

                    <Autocomplete
                      options={packages}
                      value={formik.values.package}
                      onChange={(_, val) => formik.setFieldValue("package", val)}
                      onBlur={() => formik.setFieldTouched("package", true)}
                      getOptionLabel={(o) =>
                        `${o.packageName} ($${Number(o.price || 0).toLocaleString()})`
                      }
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Select package"
                          error={formik.touched.package && Boolean(formik.errors.package)}
                          helperText={
                            formik.touched.package ? (formik.errors.package as any) : ""
                          }
                        />
                      )}
                    />
                  </Box>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2} className="booking-package__row">
                      <Grid item xs={12} sm={6}>
                        <Box className="booking-package__fieldLabelRow">
                          <CalendarMonthOutlinedIcon className="booking-package__fieldIcon" />
                          <Typography className="booking-package__fieldLabel">
                            Service Date{" "}
                            {REQUIRE_DATE ? (
                              <span className="booking-package__required">*</span>
                            ) : null}
                          </Typography>
                        </Box>
                        <DatePicker
                          value={formik.values.serviceDate}
                          onChange={(newValue) => {
                            formik.setFieldValue("serviceDate", newValue, true);
                            formik.setFieldTouched("serviceDate", true, false);
                          }}
                          minDate={dayjs()}
                          format="MM/DD/YYYY"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              error:
                                formik.touched.serviceDate &&
                                Boolean(formik.errors.serviceDate),
                              helperText: formik.touched.serviceDate
                                ? (formik.errors.serviceDate as string)
                                : "",
                              onBlur: () => formik.setFieldTouched("serviceDate", true, true),
                            },
                            popper: {
                              placement: "top-start", // KEY FIX
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className="booking-package__fieldLabelRow">
                          <AccessTimeOutlinedIcon className="booking-package__fieldIcon" />
                          <Typography className="booking-package__fieldLabel">
                            Service Time{" "}
                            {REQUIRE_TIME ? (
                              <span className="booking-package__required">*</span>
                            ) : null}
                          </Typography>
                        </Box>

                     <TimePicker
                        value={formik.values.serviceTime}
                        onChange={(newValue) => {
                          formik.setFieldValue("serviceTime", newValue, true);
                          formik.setFieldTouched("serviceTime", true, false);
                        }}
                        closeOnSelect={false}
                        disablePast={isSelectedDateToday}
                        minTime={isSelectedDateToday ? dayjs() : undefined}
                        shouldDisableTime={shouldDisableTime}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            placeholder: "Select time",
                            error:
                              formik.touched.serviceTime &&
                              Boolean(formik.errors.serviceTime),
                            helperText: formik.touched.serviceTime
                              ? (formik.errors.serviceTime as string)
                              : "",
                            onBlur: () => formik.setFieldTouched("serviceTime", true, true),
                          },
                        }}
                      />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>

                  <Box className="booking-package__actions">
                    <Button
                      variant="outlined"
                      className="booking-package__secondaryBtn"
                      onClick={onBack}
                      disabled={formik.isSubmitting}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      className="booking-package__primaryBtn"
                      disabled={formik.isSubmitting}
                    >
                      {formik.isSubmitting ? (
                        <Box className="booking-package__btnLoading">
                          <CircularProgress size={18} />
                          <span>Sending...</span>
                        </Box>
                      ) : (
                        <Box component="span" className="booking-package__primaryBtnContent">
                          <img src={PaymentIcon} alt="" className="booking-package__paymentIcon" />
                          Send Payment Link
                        </Box>
                      )}
                    </Button>
                  </Box>
                </form>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper elevation={0} className="booking-package__summaryCard">
                <Typography className="booking-package__summaryTitle">
                  Order Summary
                </Typography>

                <Box className="booking-package__summaryBlock">
                  <Typography className="booking-package__summarySectionLabel">
                    PACKAGE SUMMARY
                  </Typography>

                  {!selectedPackage ? (
                    <Typography className="booking-package__muted">
                      No package selected
                    </Typography>
                  ) : (
                    <Box className="booking-package__summaryLines">
                      <Box className="booking-package__summaryLine">
                        <Typography className="booking-package__summaryText">
                          {selectedPackage.packageName}
                        </Typography>
                        <Typography className="booking-package__summaryText">
                          ${Number(selectedPackage.price || 0).toLocaleString()}
                        </Typography>
                      </Box>

                      {selectedPackage.categories?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          {selectedPackage.categories.map((category) => (
                            <Typography
                              key={category.id}
                              className="booking-package__muted"
                              sx={{ mb: 0.5 }}
                            >
                              • {category.name}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <Divider className="booking-package__summaryDivider" />

                <Box className="booking-package__totalRow">
                  <Typography className="booking-package__totalLabel">
                    Total Amount
                  </Typography>
                  <Typography className="booking-package__totalValue">
                    ${Number(totalAmount || 0).toLocaleString()}
                  </Typography>
                </Box>

                <Box className="booking-package__paymentInfo">
                  <Typography className="booking-package__paymentInfoTitle">
                    💳 Payment Link:
                  </Typography>
                  <Typography className="booking-package__paymentInfoText">
                    A secure payment link will be sent to the lead&apos;s email after submission.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snack.type}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingPackage;